import { useI18N, VenueDetailsBySlugResponse, useAuth } from "common/hooks";
import { StandardButton } from "@/components/common/StandardButton";
import { useDarkMode } from "@/hooks/useDarkMode";
import { useState, useEffect } from "react";
import Modal from "react-responsive-modal";
import AddClaim from "./AddClaim";
import ModalWithAction from "@/components/common/ModalWithAction";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import { ChevronDown } from "lucide-react";
import ReactSmoothCollapse from "react-smooth-collapse";
import { cn } from "@/lib/utils";
import { BriefcaseIcon } from "@phosphor-icons/react";
import { createPageAnalytics, trackEvent } from "common/analytics";

type VenueInfoClaimProps = {
  venueDetailsResponse: VenueDetailsBySlugResponse;
  onRefresh?: () => void;
};

function VenueInfoClaim({
  venueDetailsResponse,
  onRefresh,
}: VenueInfoClaimProps) {
  const i18n = useI18N();
  const { isLoading: isUserDataLoading, isLoggedIn, user } = useAuth();
  const showMapperView =
    isLoggedIn &&
    (user?.roles?.isSociabilityMapper || user?.roles?.isSociabilityTeam);
  const isDark = useDarkMode();
  const [addClaimOpenModal, setAddClaimOpenModal] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [claimInfoExpanded, setClaimInfoExpanded] = useState(false);
  const [isClaimJustSubmitted, setIsClaimJustSubmitted] = useState(false);
  const { showModal } = useAuthModals();

  // place-page claim CTA + claim_business_form close chokepoint. The claim form
  // is a shared widget (also opened from the flag flow), so its close uses the
  // trackEvent primitive; the CTA is place-scoped via createPageAnalytics.
  const placeAnalytics = createPageAnalytics("place");
  const claimBusinessId = venueDetailsResponse.details.business.id.toString();
  const claimPlaceName = venueDetailsResponse.details.name;
  // brandId is a number at runtime (the type lies "string"); brand_id must be a string.
  const claimBrandId =
    venueDetailsResponse.details.brandId != null
      ? String(venueDetailsResponse.details.brandId)
      : "";
  const claimBrandName = venueDetailsResponse.details.brandName ?? "";

  // Initialize the claim submitted state from localStorage
  useEffect(() => {
    if (!isLoggedIn) {
      try {
        const storedClaimsStr = localStorage.getItem("claimed_venues") || "[]";
        const storedClaims = JSON.parse(storedClaimsStr);
        if (
          storedClaims.includes(
            venueDetailsResponse.details.business.id.toString(),
          )
        ) {
          setIsClaimJustSubmitted(true);
        }
      } catch (e) {
        console.error("Error parsing claimed venues from localStorage", e);
      }
    }
  }, [isLoggedIn, venueDetailsResponse.details.business.id]);

  // Effect to clear localStorage when user logs in
  useEffect(() => {
    if (isLoggedIn) {
      // Clear any stored claim submissions when user logs in
      localStorage.removeItem("claimed_venues");
    }
  }, [isLoggedIn]);

  // CheckIcon if current user has submitted a claim
  const currentUserHasClaim = () => {
    if (!isLoggedIn || !user) return false;

    return venueDetailsResponse.details.businessClaimData.some((claim) =>
      // Try to match by user_id first, fallback to email if user_id not available
      claim.user_id ? claim.user_id === user.id : claim.email === user.email,
    );
  };

  // CheckIcon if any claims exist (for mapper view)
  const hasAnyClaims =
    venueDetailsResponse.details.businessClaimData.length > 0;

  const handleAddClaimModalOpen = () => {
    // Claim CTA tapped — place_claim_cta_activated.
    placeAnalytics.track("claim_business_link_button", {
      brand_id: claimBrandId,
      brand_name: claimBrandName,
      business_id: claimBusinessId,
      place_name: claimPlaceName,
    });
    setAddClaimOpenModal(true);
  };

  const handleAddClaimModalClose = (isSuccess?: boolean) => {
    // Form dismissed without submitting — claim_business_form_closed.
    // A submit (success or failure) passes isSuccess and fires business_claimed
    // instead, so guard against double-counting it as a close.
    if (isSuccess === undefined) {
      trackEvent("claim_business_form_closed", {
        brand_id: claimBrandId,
        brand_name: claimBrandName,
        business_id: claimBusinessId,
        place_name: claimPlaceName,
      });
    }
    setAddClaimOpenModal(false);

    // Only show auth modal if submission was successful AND user is not logged in
    if (isSuccess && !isUserDataLoading && !isLoggedIn) {
      setIsClaimJustSubmitted(true);
      setIsModalOpen(true);
    } else {
      setIsModalOpen(false);
    }
  };

  const toggleClaimInfoExpanded = () => {
    setClaimInfoExpanded(!claimInfoExpanded);
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      return dateString; // Return original string if parsing fails
    }
  };

  return (
    <>
      {!currentUserHasClaim() && !isClaimJustSubmitted && (
        <div className="block md:col-span-12">
          <section role="none">
            <div className="w-full self-stretch min-w-11 min-h-11">
              {/* Mobile and small screens layout */}
              <div className="flex flex-wrap items-center md:hidden">
                {/* BriefcaseIcon and yourBusiness with gap-4 */}
                <div className="flex items-center gap-4 flex-shrink-0">
                  <div className="h-6 flex justify-center items-center">
                    <div
                      data-format="Outline"
                      data-weight="Regular"
                      className="w-6 h-6 relative rounded-[40px]"
                    >
                      <BriefcaseIcon
                        size={24}
                        className="text-fg-neutral-subtle"
                      />
                    </div>
                  </div>
                  <div className="min-h-6 inline-flex flex-col justify-center items-start">
                    <div className="justify-center text-fg-neutral-strong label-m-medium">
                      {i18n.get("yourBusiness")}
                    </div>
                  </div>
                </div>
                {/* Button - appears inline on 375px+ screens, wraps below 375px */}
                <div className="flex-1 flex justify-start px-1">
                  <StandardButton
                    variant="blueLink"
                    onClick={handleAddClaimModalOpen}
                    tabIndex={0}
                    className="focus-blue label-m-medium text-fg-brand-subtle"
                  >
                    {i18n.get("claimItNow")}
                  </StandardButton>
                </div>
              </div>

              {/* Larger screens layout */}
              <div className="hidden md:flex justify-between items-center">
                {/* BriefcaseIcon and yourBusiness with gap-2 */}
                <div className="flex items-center gap-4">
                  <div className="h-6 flex justify-center items-center">
                    <div
                      data-format="Outline"
                      data-weight="Regular"
                      className="w-6 h-6 relative rounded-[40px]"
                    >
                      <BriefcaseIcon
                        size={24}
                        className="text-fg-neutral-subtle"
                      />
                    </div>
                  </div>
                  <div className="min-h-6 inline-flex flex-col justify-center items-start">
                    <div className="justify-center text-fg-neutral-strong label-m-medium">
                      {i18n.get("yourBusiness")}
                    </div>
                  </div>
                </div>
                {/* Button at justify-end with primary variant */}
                <div>
                  <StandardButton
                    variant="blueLink"
                    onClick={handleAddClaimModalOpen}
                    tabIndex={0}
                    className="focus-blue label-m-medium text-fg-brand-subtle"
                  >
                    {i18n.get("claimItNow")}
                  </StandardButton>
                </div>
              </div>
            </div>
          </section>
        </div>
      )}

      {(currentUserHasClaim() || isClaimJustSubmitted) && !showMapperView && (
        <div className="md:col-span-12 mb-2">
          <section role="none">
            <div className="w-full self-stretch min-w-11 min-h-11 py-4">
              {/* Mobile and small screens layout */}
              <div className="flex flex-wrap items-center md:hidden">
                {/* BriefcaseIcon and yourBusiness with gap-4 */}
                <div className="flex items-center gap-4 flex-shrink-0">
                  <div className="h-6 flex justify-center items-center">
                    <div
                      data-format="Outline"
                      data-weight="Regular"
                      className="w-6 h-6 relative rounded-[40px]"
                    >
                      <img
                        src={
                          isDark
                            ? "/images/BriefcaseIcon_Dark.svg"
                            : "/images/Briefcase.svg"
                        }
                        alt="BriefcaseIcon"
                      />
                    </div>
                  </div>
                  <div className="min-h-6 inline-flex flex-col justify-center items-start">
                    <div className="justify-center label-m-medium">
                      {i18n.get("yourBusiness")}
                    </div>
                  </div>
                </div>
                {/* Button - appears inline on 375px+ screens, wraps below 375px */}
                <div className="flex-1 flex justify-start px-1 label-m-medium text-fg-neutral-subtle">
                  Claim submitted
                </div>
              </div>

              {/* Larger screens layout */}
              <div className="hidden md:flex justify-between items-center">
                {/* BriefcaseIcon and yourBusiness with gap-4 */}
                <div className="flex items-center gap-4">
                  <div className="h-6 flex justify-center items-center">
                    <div
                      data-format="Outline"
                      data-weight="Regular"
                      className="w-6 h-6 relative rounded-[40px]"
                    >
                      <img
                        src={
                          isDark
                            ? "/images/BriefcaseIcon_Dark.svg"
                            : "/images/Briefcase.svg"
                        }
                        alt="BriefcaseIcon"
                      />
                    </div>
                  </div>
                  <div className="min-h-6 inline-flex flex-col justify-center items-start">
                    <div className="justify-center label-m-medium">
                      {i18n.get("yourBusiness")}
                    </div>
                  </div>
                </div>
                {/* Button at justify-end with primary variant */}
                <div className="flex-1 flex justify-end px-1 label-m-medium text-fg-neutral-subtle">
                  Claim submitted
                </div>
              </div>
            </div>
          </section>
        </div>
      )}

      {hasAnyClaims && showMapperView && (
        <div className="md:col-span-12 mb-4">
          <section role="none">
            <div className="w-full self-stretch min-w-11 min-h-11 py-4">
              {/* Mobile and small screens layout */}
              <div
                className="flex flex-wrap items-center md:hidden cursor-pointer"
                onClick={toggleClaimInfoExpanded}
              >
                {/* InfoIcon icon and businessClaimed with gap-4 */}
                <div className="flex items-center gap-4 flex-shrink-0">
                  <div className="h-6 flex justify-center items-center">
                    <div
                      data-format="Outline"
                      data-weight="Regular"
                      className="w-6 h-6 relative rounded-[40px]"
                    >
                      <img
                        src={
                          isDark
                            ? "/images/infoCircle_Dark.svg"
                            : "/images/infoCircle.svg"
                        }
                        alt="InfoIcon"
                        aria-hidden="true"
                      />
                    </div>
                  </div>
                  <div className="min-h-6 inline-flex flex-col justify-center items-start">
                    <div className="justify-center label-m-medium">
                      {i18n.get("businessClaimed")}
                    </div>
                  </div>
                </div>
                {/* Chevron for expand/collapse */}
                <div className="flex-1 flex justify-end pr-4">
                  <ChevronDown
                    size={24}
                    className={cn(
                      claimInfoExpanded
                        ? "transition rotate-180"
                        : "transition",
                      "ml-4",
                    )}
                    aria-hidden="true"
                  />
                </div>
              </div>

              {/* Larger screens layout */}
              <div
                className="hidden md:flex justify-between items-center cursor-pointer focus-blue"
                onClick={toggleClaimInfoExpanded}
                tabIndex={0}
              >
                {/* InfoIcon icon and businessClaimed with gap-4 */}
                <div className="flex items-center gap-4">
                  <div className="h-6 flex justify-center items-center">
                    <div
                      data-format="Outline"
                      data-weight="Regular"
                      className="w-6 h-6 relative rounded-[40px]"
                    >
                      <img
                        src={
                          isDark
                            ? "/images/infoCircle_Dark.svg"
                            : "/images/infoCircle.svg"
                        }
                        alt="InfoIcon"
                        aria-hidden="true"
                      />
                    </div>
                  </div>
                  <div className="min-h-6 inline-flex flex-col justify-center items-start">
                    <div className="justify-center label-m-medium">
                      {i18n.get("businessClaimed")}
                    </div>
                  </div>

                  {/* Chevron for expand/collapse */}
                  <ChevronDown
                    size={24}
                    className={cn(
                      claimInfoExpanded
                        ? "transition rotate-180"
                        : "transition",
                      "ml-4",
                    )}
                    aria-hidden="true"
                  />
                </div>
              </div>

              {/* Collapsible claimed by information */}
              <ReactSmoothCollapse expanded={claimInfoExpanded}>
                <div className="self-stretch inline-flex flex-col justify-start items-start gap-4 mt-4">
                  {venueDetailsResponse.details.businessClaimData.map(
                    (claim) => (
                      <div
                        key={claim.id}
                        className="w-full flex-1 min-h-6 inline-flex flex-col justify-center items-start border-b border-border-token-neutral-minimal pb-4 last:border-b-0"
                      >
                        <div className="self-stretch justify-center">
                          <span className="text-fg-neutral-subtle body-s-regular">
                            {formatDate(claim.created_at)} •
                          </span>
                          <span className="text-fg-neutral-subtle label-s-medium">
                            {" "}
                            {claim.name}
                          </span>
                        </div>
                        <div className="self-stretch justify-start label-m-medium text-fg-neutral-strong mt-1">
                          {claim.description}
                        </div>
                      </div>
                    ),
                  )}
                </div>
              </ReactSmoothCollapse>
            </div>
          </section>
        </div>
      )}

      {addClaimOpenModal && (
        <Modal
          focusTrapped
          center
          blockScroll={true}
          animationDuration={0}
          open={addClaimOpenModal}
          onClose={() => handleAddClaimModalClose()}
          classNames={{
            modal:
              "bg-[transparent] p-0 focus:outline-0 shadow-none md:w-[480px]",
            closeButton: "hidden",
          }}
        >
          <AddClaim
            venueDetailsResponse={venueDetailsResponse}
            onClose={handleAddClaimModalClose}
            onInfoClick={() => {}}
            flagVenueAccessibilityId={null}
            onRefresh={onRefresh}
          />
        </Modal>
      )}

      <ModalWithAction
        isModalOpen={isModalOpen}
        title={i18n.get("claimSubmitted")}
        subtitle={i18n.get("claimSubmittedText")}
        cancelButtonTitle={i18n.get("later")}
        actionButtonTitle={i18n.get("continue")}
        onClose={() => {
          setIsModalOpen(false);
        }}
        onClick={() => {
          setIsModalOpen(false);
          showModal(AuthModalScreen.EnterEmail);
        }}
        type="success"
        actionButtonType="default"
      />
    </>
  );
}

export default VenueInfoClaim;
